<template>
  <div class="py-[1.16rem]">
    <!-- Display Error Message if Any -->
    <div v-if="error" class="text-red-500 h-[70vh] flex justify-center items-center">
      <p>An error occurred: {{ error }}</p>
    </div>

    <!-- Header and Actions -->
    <div
      v-else
      class="bg-pitch-black flex justify-between items-center pb-4 px-5 border-b-2 border-rose-light"
    >
      <!-- Filter Icon Button -->
      <button @click="toggleFilter">
        <img
          v-if="!isFilterApplied"
          class="w-6 h-6"
          src="@/assets/images/bars-filter.svg"
        />
        <img
          v-if="isFilterApplied"
          class="w-6 h-6"
          src="@/assets/images/bars-filter-inverted.svg"
        />
      </button>

      <!-- Filter Text -->
      <div class="flex-1 flex justify-start ml-4">
        <p v-if="!showSearch" class="text-gray-400 text-xs">
          {{
            isFilterApplied
              ? `Showing filtered threads (${filteredThreads?.length || 0})`
              : `Showing all threads (${filteredThreads?.length || 0})`
          }}
          <span v-if="unreadCount > 0"> - {{ unreadCount }} unread</span>
        </p>
        <div v-else class="mt-0 flex-1 p-0">
          <input
            type="text"
            v-model="searchQuery"
            @input="handleSearch"
            class="w-full bg-pitch-black outline-none rounded-md text-sm text-total-white"
            placeholder="Search logs..."
          />
        </div>
      </div>

      <!-- Icons Section -->
      <div class="flex items-center space-x-5">
        <!-- Search Icon -->
        <button v-if="!showSearch" @click="toggleSearch" class="text-prime-golden">
          <i class="fa fa-search text-lg"></i>
        </button>

        <!-- Close Search Icon -->
        <button v-else @click="toggleSearch" class="text-prime-golden">
          <div class="bg-gray-500 rounded-full w-4 h-4 flex items-center justify-center">
            <i class="fas fa-times text-xs text-black"></i>
          </div>
        </button>
      </div>

      <!-- Filter Overlay -->
      <div
        v-if="showFilter"
        @click.self="toggleFilter"
        class="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50"
      >
        <div class="bg-pitch-black p-6 px-8 rounded-lg shadow-lg w-96 relative">
          <div class="flex justify-between items-center mb-4">
            <h2 class="text-2xl font-bold text-total-white">Filter Logs</h2>
            <button @click="toggleFilter" class="text-gray-400">
              <i class="fas fa-times"></i>
            </button>
          </div>
          <p class="text-sm text-gray-400 text-left">
            Select the log types you want to see.
          </p>

          <!-- 1) Email, SMS, Call (front-end) -->
          <div class="flex flex-col gap-3 mb-4 mt-4">
            <!-- Email -->
            <label for="filter_email" class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="filters.email"
                id="filter_email"
                class="hidden"
                @change="handleFilterChange"
              />
              <div
                :class="[
                  'w-6 h-6 rounded flex items-center justify-center border cursor-pointer',
                  filters.email
                    ? 'bg-dark-grey border-gray-700'
                    : 'bg-gray-600 border-gray-700',
                ]"
              >
                <svg
                  v-if="filters.email"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-prime-golden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span class="text-base font-medium text-total-white">Email</span>
            </label>

            <!-- SMS -->
            <label for="filter_sms" class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="filters.sms"
                id="filter_sms"
                class="hidden"
                @change="handleFilterChange"
              />
              <div
                :class="[
                  'w-6 h-6 rounded flex items-center justify-center border cursor-pointer',
                  filters.sms
                    ? 'bg-dark-grey border-gray-700'
                    : 'bg-gray-600 border-gray-700',
                ]"
              >
                <svg
                  v-if="filters.sms"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-prime-golden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span class="text-base font-medium text-total-white">SMS</span>
            </label>

            <!-- Call -->
            <label for="filter_call" class="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                v-model="filters.call"
                id="filter_call"
                class="hidden"
                @change="handleFilterChange"
              />
              <div
                :class="[
                  'w-6 h-6 rounded flex items-center justify-center border cursor-pointer',
                  filters.call
                    ? 'bg-dark-grey border-gray-700'
                    : 'bg-gray-600 border-gray-700',
                ]"
              >
                <svg
                  v-if="filters.call"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-prime-golden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span class="text-base font-medium text-total-white">Call</span>
            </label>
          </div>

          <!-- 2) Calendar & System (Back-end toggles) -->
          <div class="flex flex-col gap-3 mb-4">
            <!-- Calendar -->
            <label
              for="filter_calendar"
              class="flex items-center gap-3 cursor-pointer"
              @change="toggleCalendar($event.target.checked)"
            >
              <!-- We do not use v-model here because toggling triggers an API call. -->
              <input
                type="checkbox"
                :checked="showCalendar"
                id="filter_calendar"
                class="hidden"
              />
              <div
                :class="[
                  'w-6 h-6 rounded flex items-center justify-center border cursor-pointer',
                  showCalendar
                    ? 'bg-dark-grey border-gray-700'
                    : 'bg-gray-600 border-gray-700',
                ]"
              >
                <svg
                  v-if="showCalendar"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-prime-golden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span class="text-base font-medium text-total-white">Calendar</span>
            </label>

            <!-- System -->
            <label
              v-if="isAdmin"
              for="filter_system"
              class="flex items-center gap-3 cursor-pointer"
              @change="toggleAgentSystem($event.target.checked)"
            >
              <input
                type="checkbox"
                :checked="showAgentSystem"
                id="filter_system"
                class="hidden"
              />
              <div
                :class="[
                  'w-6 h-6 rounded flex items-center justify-center border cursor-pointer',
                  showAgentSystem
                    ? 'bg-dark-grey border-gray-700'
                    : 'bg-gray-600 border-gray-700',
                ]"
              >
                <svg
                  v-if="showAgentSystem"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5 text-prime-golden"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  stroke-width="3"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span class="text-base font-medium text-total-white">System</span>
            </label>
          </div>

          <!-- Buttons for Select All / Clear All -->
          <div class="flex justify-end gap-4 mb-4">
            <button
              class="bg-dark-grey text-prime-golden px-3 py-1 rounded text-sm font-semibold"
              @click="selectAllFilters"
            >
              Select All
            </button>
            <button
              class="bg-dark-grey text-prime-golden px-3 py-1 rounded text-sm font-semibold"
              @click="clearAllFilters"
            >
              Deselect All
            </button>
          </div>

          <!-- Done / Close Filter -->
          <!--<button
            class="bg-prime-golden-800 text-sm font-semibold text-pitch-black px-3 py-1 rounded"
            @click="toggleFilter"
          >
            Done
          </button>-->
        </div>
      </div>
    </div>

    <!-- Log List -->
    <div
      :class="[
        'cards-section-height overflow-y-auto pb-[100px]',
        { 'opacity-50': isFetching },
      ]"
      v-if="!error && filteredThreads?.length && !isFirstLoad"
    >
      <!--<LogCard
        v-for="log in filteredLogs"
        :key="log.id"
        :title="getLogTitle(log)"
        :date="formatDate(log.date_time_created)"
        :message="log.content"
        :type="log.comm_type"
        :isNew="logStore.updatedThreadIds.includes(log.thread_id)"
        :subject="log.comm_type === 'sms' ? 'Text thread' : log.subject"
        @logClicked="handleLogClick"
        :id="log.id"
        :thread_id="log.thread_id"
      />-->
      <div class="h-3"></div>
      <LogCard
        v-for="thread in filteredThreads"
        :key="thread.id"
        :title="getLogTitle(thread)"
        :date="formatDate(thread.last_updated_at)"
        :message="getMessage(thread)"
        :type="thread.comm_type"
        :isNew="logStore.updatedThreadIds.includes(thread.id)"
        :subject="
          thread.comm_type === 'sms'
            ? 'Text thread'
            : thread.latest_communication?.subject || 'Thread'
        "
        @logClicked="handleLogClick"
        :id="thread.id"
        :thread_id="thread.id"
      />
    </div>

    <!-- No Logs Found Message -->
    <div v-else-if="!error">
      <p
        v-if="isFetching || isFirstLoad"
        class="text-gray-400 h-[65vh] flex justify-center items-center"
      >
        Fetching logs...
      </p>
      <p
        v-else-if="
          (searchQuery && !filteredThreads?.length) ||
          (isFilterApplied && !filteredThreads?.length)
        "
        class="text-gray-400 h-[65vh] flex justify-center items-center"
      >
        No logs match your search.
      </p>
      <p v-else class="text-gray-400 h-[65vh] flex justify-center items-center">
        <button
          @click="toggleCommDrawer"
          class="text-prime-golden flex items-center gap-3"
        >
          <img src="@/assets/images/add-icon.svg" class="w-6 h-6" />
          Click to add a log!
        </button>
      </p>
    </div>

    <!-- Pagination Controls -->
    <div
      v-if="!isFirstLoad"
      class="flex justify-center items-center py-4 bg-pitch-black-900 fixed bottom-0 w-full border-t border-gray-600"
    >
      <button
        @click="previousPage"
        :disabled="currentPage == 1"
        class="text-prime-golden px-2 disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-left"></i>
      </button>
      <span class="text-sm text-total-white mx-4">
        <template v-if="!isFirstLoad">{{ currentPage }}/{{ totalPages }}</template>
      </span>
      <button
        @click="nextPage"
        :disabled="!hasMoreLogs"
        class="text-prime-golden px-2 disabled:text-prime-golden-dark"
      >
        <i class="fa fa-chevron-right"></i>
      </button>
    </div>

    <!-- Add Communication Drawer -->
    <CommunicationDrawer
      :isOpen="isCommDrawerOpen"
      :projectId="route.params.id"
      @close="toggleCommDrawer"
      @emailSelected="handleEmailSelected"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useRoute, useRouter } from "vue-router";
import LogCard from "@/components/LogCard.vue";
import { useLogStore } from "@/stores/log";
import { useContactsStore } from "@/stores/contact";
import CommunicationDrawer from "@/components/CommunicationDrawer.vue";
import { useUserStore } from "@/stores/user";
import { useProjectStore } from "@/stores/project";

const logStore = useLogStore();
const contactStore = useContactsStore();
const router = useRouter();
const route = useRoute();

const showSearch = ref(false);
const searchQuery = ref("");
const error = ref(null);

const isFetching = ref(false);
const refreshInterval = ref(null);
const isFirstLoad = ref(true);

const total = computed(() => logStore.totalThreads);
const currentPage = computed(() => logStore.currentPage);
const totalPages = computed(() => Math.max(1, Math.ceil(total.value / logStore.size)));
const showFilter = ref(false);
const userStore = useUserStore();
const projectStore = useProjectStore();

const filters = ref({
  email: true,
  sms: true,
  call: true,
});
// Has the user enabled system logs in localStorage?
const showCalendar = ref(false);
const showAgentSystem = ref(false);

// Is the user an admin?
const isAdmin = computed(() => userStore.profile?.account_type === "admin");

const projectContacts = ref([]);

const hasMoreLogs = computed(() => {
  return searchQuery.value.trim()
    ? currentPage.value < totalPages.value
    : currentPage.value * logStore.size < total.value;
});

const selectedLogType = ref(""); // Default value for log type filter

const fetchData = async (page = currentPage.value) => {
  if (isFirstLoad.value) {
    isFetching.value = true;
  }
  try {
    const projectId = route.params.id;
    //await logStore.fetchLogsByProjectId(projectId, page);
    await logStore.fetchThreadsByProjectId(
      projectId,
      selectedLogType.value,
      "desc",
      page
    );

    await logStore.fetchNewLogsByProjectId(projectId);

    if (isFirstLoad.value) {
      //console.log("Fetching project contacts!!!:", projectId);
      projectContacts.value = await contactStore.fetchContactsByProjectId(projectId);
      //console.log("Project contacts fetched!!!: ", projectContacts.value);
      isFetching.value = false;
      isFirstLoad.value = false;
    }
  } catch (err) {
    error.value = "Failed to load logs. Please try again later.";
    console.error(err);
    if (isFirstLoad.value) {
      isFetching.value = false;
      isFirstLoad.value = false;
    }
  }
};
async function toggleCalendar() {
  showCalendar.value = !showCalendar.value;
  try {
    await projectStore.updateProject(route.params.id, {
      show_calendar: showCalendar.value,
    });
    // Re-fetch logs from server
    await fetchData(logStore.currentPage);
  } catch (err) {
    console.error("Failed to update show_calendar:", err);
  }
}

// Toggling "System" => update the project and re-fetch
async function toggleAgentSystem() {
  showAgentSystem.value = !showAgentSystem.value;
  try {
    await projectStore.updateProject(route.params.id, {
      show_agent_system: showAgentSystem.value,
    });
    // Re-fetch logs from server
    await fetchData(logStore.currentPage);
  } catch (err) {
    console.error("Failed to update show_agent_system:", err);
  }
}
async function fetchProjectInfo(projectId) {
  // fetch the project from the store
  await projectStore.fetchProjectById(projectId);
  const p = projectStore.currentProject;
  if (!p) return;
  // set local states for show_calendar, show_agent_system
  showCalendar.value = !!p.show_calendar;
  showAgentSystem.value = !!p.show_agent_system;
}
function selectAllFilters() {
  filters.value.email = true;
  filters.value.sms = true;
  filters.value.call = true;
  saveFiltersToLocalStorage(); // Save filters to localStorage
  handleSearch(); // Apply the filter changes immediately

  // Also turn on calendar/system if user is admin
  console.log(
    projectStore.currentProject,
    "projectStore.currentProject?.show_agent_system,",
    showAgentSystem.value
  );
  // Then call the project update endpoints for the booleans that changed:
  if (!showCalendar.value) {
    toggleCalendar(); // triggers an update + fetch
  }
  if (isAdmin.value && !showAgentSystem.value) {
    toggleAgentSystem();
  }
}
function clearAllFilters() {
  filters.value.email = false;
  filters.value.sms = false;
  filters.value.call = false;
  saveFiltersToLocalStorage(); // Save filters to localStorage
  handleSearch(); // Apply the filter changes immediately

  // Also turn off calendar/system if user is admin
  if (showCalendar.value) {
    toggleCalendar(); // triggers an update + fetch
  }

  if (isAdmin.value && showAgentSystem.value) {
    toggleAgentSystem();
  }
}
onMounted(async () => {
  logStore.clearData();
  const projectId = route.params.id;
  loadFiltersFromLocalStorage();
  await fetchProjectInfo(projectId);
  await fetchData();
  clearInterval(refreshInterval.value);
  refreshInterval.value = setInterval(() => {
    // subsequent refreshes => do not set isFetching
    fetchData(currentPage.value);
  }, 3000);
});

onBeforeUnmount(() => {
  if (refreshInterval.value) {
    clearInterval(refreshInterval.value);
  }
});

const isFilterApplied = computed(() => {
  if (!filters.value.email || !filters.value.sms || !filters.value.call) return true;

  if (!showCalendar.value || !showAgentSystem.value) return true;
  return false;
});

const toggleFilter = () => {
  showFilter.value = !showFilter.value;
  handleSearch();
};

/*ilters = () => {
  selectedLogType.value = "all";
  searchQuery.value = "";
  handleSearch();
};*/

const getMessage = (thread) => {
  if (thread.comm_type == "call") {
    return thread.latest_communication.subject;
  } else if (thread.comm_type == "calendar") {
    if (thread.latest_communication?.content) {
      return thread.latest_communication.content;
    } else {
      return thread.communications?.[0].content;
    }
  } else if (thread.latest_communication?.content) {
    let content = thread.latest_communication.content;
    
    // For email content, clean up HTML tags and normalize whitespace
    if (thread.comm_type === "email") {
      content = content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/&nbsp;/g, ' ') // Replace &nbsp; with regular spaces
        .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
        .trim(); // Remove leading/trailing whitespace
    }
    
    return content;
  } else if (thread.latest_communication?.media_urls?.length > 0) {
    return "📎" + "\u00A0" + "\u00A0" + "File Attachment";
  }

  return "No content";
};

const handleSearch = async () => {
  try {
    const projectId = route.params.id;
    if (searchQuery.value.trim()) {
      await logStore.searchThreads(
        projectId,
        searchQuery.value.trim(),
        selectedLogType.value
      );
    } else {
      await logStore.fetchThreadsByProjectId(projectId, selectedLogType.value);
    }
  } catch (err) {
    error.value = "An error occurred while searching logs.";
    console.error("Error during search:", err);
  }
};

const previousPage = async () => {
  if (currentPage.value > 1) {
    const projectId = route.params.id;
    const newPage = currentPage.value - 1;

    try {
      if (searchQuery.value.trim()) {
        // In search mode, call the search endpoint
        await logStore.searchThreads(
          projectId,
          searchQuery.value.trim(),
          selectedLogType.value,
          newPage
        );
      } else {
        // In normal mode, fetch logs without search
        await fetchData(newPage);
      }
    } catch (error) {
      console.error("Error fetching previous page logs:", error);
    }
  }
};

const nextPage = async () => {
  if (hasMoreLogs.value) {
    const projectId = route.params.id;
    const newPage = currentPage.value + 1;

    try {
      if (searchQuery.value.trim()) {
        // In search mode, call the search endpoint
        await logStore.searchThreads(
          projectId,
          searchQuery.value.trim(),
          selectedLogType.value,
          newPage
        );
      } else {
        // In normal mode, fetch logs without search
        await fetchData(newPage);
      }
    } catch (error) {
      console.error("Error fetching next page logs:", error);
    }
  }
};

const toggleSearch = () => {
  showSearch.value = !showSearch.value;
  if (!showSearch.value) {
    searchQuery.value = "";
    handleSearch();
  }
};

const handleLogClick = ({ type, content, thread_id }) => {
  const routeMap = {
    email: "EmailViewPage",
    call: "CallLogPage",
    sms: "TextPage",
    calendar: "CalendarViewPage",
    agent_system: "SystemLogPage",
  };

  router.push({
    name: routeMap[type],
    params: { projectId: route.params.id, threadId: thread_id, fromLog: true },
    query: { content, tab: "log", thread_id },
  });
};

const filteredThreads = computed(() => {
  //const query = searchQuery.value?.toLowerCase();
  let results = threads.value;

  results = results.filter((t) => {
    if (t.comm_type === "email" && !filters.value.email) return false;
    if (t.comm_type === "sms" && !filters.value.sms) return false;
    if (t.comm_type === "call" && !filters.value.call) return false;
    // For agent_system / calendar, the server already handled it (show/hide).
    return true;
  });

  const typeFilteredLogs = results;

  return typeFilteredLogs;

  /*
  // Apply search query filter
  if (!query) return typeFilteredLogs;

  // Filter logs based on search query (content or title)
  return typeFilteredLogs.filter(
    (log) =>
      log.content?.toLowerCase().includes(query) ||
      getLogTitle(log)?.toLowerCase().includes(query)
  );*/
});

const threads = computed(() => logStore.threads);

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const options = {
    year: "numeric",
    month: "short",
    weekday: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return date.toLocaleString(undefined, options);
};

function getLogTitle(thread) {
  if (!thread) return "";

  if (thread.comm_type === "agent_system") {
    return thread.subject || "System Logs";
  }

  const log = thread.latest_communication
    ? thread.latest_communication
    : thread.communications?.[0];

  if (!log) return "";

  try {
    const stakeholders = projectContacts.value;

    // Helper: Check if the given contact belongs to the Project Manager
    const isProjectManager = (contact) => {
      // console.log("Checking if contact is Project Manager: ", contact, stakeholders);
      const stakeholder = stakeholders.find(
        (s) => s.phone == contact || s.email == contact
      );
      return stakeholder && stakeholder.role == "Project Manager";
    };

    const getStakeholderNameByPhone = (phone) => {
      const stakeholder = stakeholders.find((s) => s.phone === phone);
      if (stakeholder?.first_name === "" && stakeholder?.last_name === "") {
        return phone;
      }
      if (stakeholder?.first_name === "Unknown" && stakeholder?.last_name === "Unknown") {
        return phone;
      }
      if (stakeholder) {
        const lastName = stakeholder.last_name?.trim();
        return lastName ? `${stakeholder.first_name} ${lastName.charAt(0)}` : stakeholder.first_name;
      }
      return phone;
    };

    const getStakeHolderNameByEmail = (email) => {
      const stakeholder = stakeholders.find((s) => s.email === email);
      if (stakeholder?.first_name === "" && stakeholder?.last_name === "") {
        return email;
      }
      if (stakeholder?.first_name === "Unknown" && stakeholder?.last_name === "Unknown") {
        return email;
      }
      if (stakeholder) {
        const lastName = stakeholder.last_name?.trim();
        return lastName ? `${stakeholder.first_name} ${lastName.charAt(0)}` : stakeholder.first_name;
      }
      return email;
    };
    if (log?.comm_type === "calendar") {
      return log.subject || "Calendar Event";
    }
    if (log.comm_type === "agent_system") {
      // e.g. always call it "System Logs" or use thread.latest_communication.subject if it exists
      return "System Logs";
    }
    if (log?.comm_type === "sms" || log?.comm_type === "call") {
      // For text/call logs, remove the Project Manager contact.

      // Get from_contact's name only if not a PM
      const fromName = isProjectManager(log?.from_contact)
        ? ""
        : getStakeholderNameByPhone(log?.from_contact)?.trim();
      let toNames = [];
      if (log?.to_contact) {
        // Check if there are multiple contacts (comma-separated)
        const toContacts = log.to_contact.includes(",")
          ? log.to_contact.split(", ")
          : [log.to_contact];
        toNames = toContacts
          .filter((contact) => !isProjectManager(contact))
          .map((contact) => getStakeholderNameByPhone(contact));
      }

      // Assemble the parts: fromName (if exists) first, then the others.
      const parts = [];
      if (fromName) parts.push(fromName);
      if (toNames.length) parts.push(...toNames);
      return parts.join(", ");
    } else if (log?.comm_type === "email") {
      // 1) Helper to parse either string or array into a clean array of addresses
      const parseContacts = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) {
          return value.map((s) => s.trim()).filter(Boolean);
        } else if (typeof value === "string") {
          return value
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
        }
        return [];
      };

      // 2) Gather addresses from to, cc, bcc
      const toArr = parseContacts(log.to_contact);
      const ccArr = parseContacts(log.cc_contact);
      const bccArr = parseContacts(log.bcc_contact);
      // 3) Helper: Check if the given contact belongs to the Project Manager
      const isProjectManager = (contact) => {
        const stakeholder = stakeholders.find(
          (s) => s.phone === contact || s.email === contact
        );
        return stakeholder && stakeholder.role === "Project Manager";
      };

      // 4) Determine the from contact name (if not PM)
      const fromName = log.from_contact
        ? isProjectManager(log.from_contact)
          ? "me"
          : getStakeHolderNameByEmail(log.from_contact)
        : "";

      // 5) Build a list of other addresses from to, cc, bcc.
      // For any address that is a PM, we substitute "me".
      let others = [...toArr, ...ccArr, ...bccArr].map((addr) =>
        isProjectManager(addr) ? "me" : getStakeHolderNameByEmail(addr)
      );

      // 6) Remove duplicates from others.
      others = others.filter((addr, index, self) => addr && self.indexOf(addr) === index);

      // 7) Sort others alphabetically.
      others.sort((a, b) => a.localeCompare(b));

      // 8) Assemble final list:
      //    If fromName exists, it is forced as first element.
      //    Then, all others follow.
      let finalList = [];
      if (fromName) {
        finalList.push(fromName);
      }
      finalList = finalList.concat(others);

      // 9) Remove duplicate "me, " from the finalList
      finalList = finalList.filter(
        (addr, index, self) => addr !== "me" || self.indexOf(addr) === index
      );

      return finalList.join(", ");
    }

    // Fallback
    return "";
  } catch (err) {
    console.error("Error getting log title:", err);
    return "..., ...";
  }
}

const isCommDrawerOpen = ref(false); // State for Communication Drawer visibility

const toggleCommDrawer = () => {
  isCommDrawerOpen.value = !isCommDrawerOpen.value; // Toggle the drawer
};

// Save and load filter settings from localStorage
const saveFiltersToLocalStorage = () => {
  localStorage.setItem("logFilters", JSON.stringify(filters.value));
};

const loadFiltersFromLocalStorage = () => {
  const savedFilters = localStorage.getItem("logFilters");
  if (savedFilters) {
    filters.value = { ...filters.value, ...JSON.parse(savedFilters) };
  }
};

// Update event listeners on checkboxes to save to localStorage
const handleFilterChange = () => {
  saveFiltersToLocalStorage();
  handleSearch();
};
</script>

<style scoped>
.cards-section-height {
  height: calc(100vh - 16rem);
}
</style>
